import React, { MouseEvent, useEffect, useRef, useState } from "react";
import "./CommonDropDown.css";

export interface CommonDropDownDisplayValue<T> {
  icon?: React.ReactNode;
  display: string;
  value: T;
  disabled: boolean;
}

type Props<T> = {
  value: T;
  id: string;
  onChange: (value: T) => void;
  dropDownDisplayValues: CommonDropDownDisplayValue<T>[];
  style?: React.CSSProperties;
  dropDownStyle?: React.CSSProperties;
  disabled?: boolean;
  hideDropIcon?: boolean;
  dropDirection?: "down" | "up";
};

type DropDownProps = {
  options: React.ReactNode;
  onBlur: () => void;
  style?: React.CSSProperties;
};

// const isNodeInsideParent = (node: Node, parent: Node) => {
//   let checkDom: Node | null = node;
//   while (checkDom) {
//     if ((checkDom as Element).innerHTML === (parent as Element).innerHTML) {
//       return true;
//     }
//     checkDom = checkDom.parentNode;
//   }
//   return false;
// };

const isDomParent = (child: Node, parent: Node) => {
  return parent.contains(child) || parent.parentNode?.contains(child);
};

const CommonDropDownOptions = ({
  options,
  onBlur = () => {},
  style = {},
}: DropDownProps) => {
  const container = useRef(null);

  const onOutsieClicked = (event: MouseEvent) => {
    const containerDom = container.current
      ? (container.current as unknown)
      : {};
    const targetDom = event.target;
    const check = isDomParent(targetDom as Node, containerDom as Node);
    if (check) {
      event.preventDefault();
    } else {
      onBlur();
    }
  };

  const savedHandler = useRef(onOutsieClicked);

  useEffect(() => {
    const eventListener = (event: any) => savedHandler.current(event);
    document.addEventListener("mousedown", eventListener);
    return () => document.removeEventListener("mousedown", eventListener);
  }, []);

  return (
    <div
      className={"common-dropdown-container common-dropdown-active"}
      ref={container}
      style={style}
    >
      {options}
    </div>
  );
};

export default function CommonDropDown<T extends string | number>({
  value,
  id,
  onChange,
  dropDownDisplayValues,
  style = {},
  dropDownStyle = {},
  disabled = false,
  hideDropIcon = false,
  dropDirection = "down",
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const selectorHeight = style.height;
  const fontSize = style.fontSize;
  const options = dropDownDisplayValues.map((dropDown) => {
    let itemClass = "common-dropdown-item";
    if (dropDown.disabled) {
      itemClass += " disabled";
    }
    return (
      <div
        key={`dropdown-${dropDown.value}`}
        className={itemClass}
        onClick={() => {
          if (!dropDown.disabled) {
            setOpen(false);
            onChange(dropDown.value);
          }
        }}
        style={{
          height: selectorHeight,
          fontSize: fontSize,
        }}
      >
        <div className="common-dropdown-icon-container">{dropDown.icon}</div>
        <div className="common-dropdown-text-container">
          <p>{dropDown.display}</p>
        </div>
      </div>
    );
  });

  let dropStyle = dropDownStyle;
  if (dropDirection === "up") {
    if (dropStyle.height) {
      if (typeof dropStyle.height === "number") {
        dropStyle = { ...dropStyle, top: `${-dropStyle.height}px` };
      } else {
        dropStyle = { ...dropStyle, top: `-${dropStyle.height}` };
      }
    } else {
      dropStyle = {
        ...dropStyle,
        top: `${dropDownDisplayValues.length * -100 + 1}%`,
      };
    }

    if (dropStyle.maxHeight) {
      if (typeof dropStyle.maxHeight === "number") {
        dropStyle = { ...dropStyle, top: `${-dropStyle.maxHeight}px` };
      } else {
        dropStyle = { ...dropStyle, top: `-${dropStyle.maxHeight}` };
      }
    } else {
      dropStyle = {
        ...dropStyle,
        top: `${dropDownDisplayValues.length * -100 + 1}%`,
      };
    }
  }

  const onClickContainer = () => {
    setOpen(!open);
  };

  const onBlur = () => {
    setOpen(false);
  };

  const displayString =
    dropDownDisplayValues.filter((d) => d.value == value)[0]?.display ?? "";

  return (
    <div
      style={{
        position: "relative",
        ...style,
      }}
    >
      <button
        className="common-dropdown-button"
        id={id}
        key={id + "container"}
        onClick={onClickContainer}
        style={style}
        disabled={disabled}
      >
        <label style={{ fontSize: fontSize }}>{displayString}</label>
        {!hideDropIcon ? (
          <svg
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.99994 6L0.669812 -7.23134e-07L9.33007 0L4.99994 6Z"
              fill="#707070"
            />
          </svg>
        ) : (
          <></>
        )}
      </button>
      {open && (
        <CommonDropDownOptions
          options={options}
          onBlur={onBlur}
          style={dropStyle}
        />
      )}
    </div>
  );
}
